/**
 * Wrapper for every page a visitor can reach without signing in.
 *
 * Loads the deployment's contact addresses and prices once and hands them to
 * the page below via the outlet context, so the legal pages and the pricing
 * page share one fetch and one source of truth.
 */
import { Link, Outlet, useOutletContext } from 'react-router-dom';
import { useApi } from '@/lib/useApi';
import type { PublicConfig } from '@shared/types';

/** Fallbacks, used only if the backend is unreachable — a legal page must still render. */
const FALLBACK: PublicConfig = {
  signup_enabled: true,
  trial_days: 7,
  demo: null,
  // Off in the fallback: if the backend cannot be reached we do not know
  // whether chat is switched on, and loading a third-party script on a guess
  // is the wrong way to be wrong.
  live_chat: { enabled: false, property_id: null, widget_id: null },
  company: { name: 'OPSERION', product: 'PROVIA', address: null, number: null },
  contact: {
    general: 'contact@opserion.uk',
    support: 'contact@opserion.uk',
    sales: 'pricing@opserion.uk',
    billing: 'contact@opserion.uk',
    privacy: 'george@opserion.uk',
    legal: 'george@opserion.uk',
  },
  pricing: {
    currency: 'GBP',
    symbol: '£',
    plans: [
      { key: 'starter', name: 'Starter', price_pence: 3000, price_display: '£30', property_limit: 5 },
      { key: 'standard', name: 'Standard', price_pence: 5000, price_display: '£50', property_limit: 10 },
      { key: 'custom', name: 'Custom', price_pence: null, price_display: null, property_limit: null },
    ],
    /*
     * One currency in the fallback, deliberately.
     *
     * This is what renders when the backend cannot be reached. Offering a
     * currency switcher built from guessed prices would be inventing numbers
     * on a page about what things cost.
     */
    currencies: [
      {
        currency: 'GBP',
        symbol: '£',
        primary: true,
        plans: {
          starter: { amount_minor: 3000, display: '£30', checkout_url: null },
          standard: { amount_minor: 5000, display: '£50', checkout_url: null },
        },
      },
    ],
  },
};

export function usePublicConfig(): PublicConfig {
  return useOutletContext<PublicConfig>();
}

const LEGAL_LINKS = [
  { to: '/pricing', label: 'Pricing' },
  { to: '/terms', label: 'Terms' },
  { to: '/privacy', label: 'Privacy' },
  { to: '/data-processing', label: 'Data processing' },
  { to: '/refunds', label: 'Refunds' },
  { to: '/cookies', label: 'Cookies' },
];

export default function PublicShell() {
  const { data } = useApi<PublicConfig>('/public/config');
  const config = data ?? FALLBACK;

  return (
    <div className="min-h-screen flex flex-col bg-bg text-ink">
      <header className="border-b border-line">
        <div className="mx-auto w-full max-w-5xl px-5 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="font-semibold tracking-tight text-lg">
            {config.company.product}
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/tour" className="text-muted hover:text-ink transition-colors">See it</Link>
            <Link to="/pricing" className="text-muted hover:text-ink transition-colors">Pricing</Link>
            <Link to="/tenant-login" className="text-muted hover:text-ink transition-colors">
              Tenant portal
            </Link>
            <Link
              to="/login"
              className="rounded-lg border border-line px-3 py-1.5 font-medium hover:bg-raised transition-colors"
            >
              Sign in
            </Link>
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <Outlet context={config} />
      </main>

      <Footer config={config} />
    </div>
  );
}

export function Footer({ config }: { config: PublicConfig }) {
  return (
    <footer className="border-t border-line mt-16">
      <div className="mx-auto w-full max-w-5xl px-5 sm:px-6 py-10 space-y-6">
        <nav className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
          {LEGAL_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="text-muted hover:text-ink transition-colors">
              {l.label}
            </Link>
          ))}
          <a href={`mailto:${config.contact.general}`} className="text-muted hover:text-ink transition-colors">
            Contact
          </a>
        </nav>

        <div className="text-sm text-muted space-y-1">
          <p>
            <span className="text-ink font-medium">{config.company.product}</span> is built and
            delivered by <span className="text-ink font-medium">{config.company.name}</span>.
          </p>
          {config.company.address && <p>{config.company.address}</p>}
          {config.company.number && <p>Company number {config.company.number}</p>}
          <p className="pt-2 text-faint">
            © {new Date().getFullYear()} {config.company.name}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
